<template>
    <div>
        <template v-if="classList !== null">
            <div class="control-panel d-flex">
                <template v-if="showMobileVersion">
                    <div class="oval mobile-checked-btn">
                        <div v-if="!readonly && accessibleItems.length"
                             class="checkbox-wrapper">
                            <checkbox :transparent="true"
                                      v-select="classesIds"
                                      :checked="checkedAll">
                            </checkbox>
                        </div>
                    </div>
                </template>
                <template v-if="isMobile">
                    <control-panel-mobile>
                        <div class="oval"
                             v-if="hasAccess(['email', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassBulkEmailModal', options: { width: '680px' } })">
                            <img src="../../../../assets/img/mail.svg">
                        </div>
                        <template v-if="env === 'development' || env === 'test'">
                            <div class="oval"
                                 v-if="hasAccess(['sms', 'class.classes'])"
                                 @click="openModal({ modalName: 'ClassBulkSMSModal' })">
                                <img src="../../../../assets/img/sms.svg">
                            </div>
                        </template>
                        <div class="oval"
                             v-if="hasAccess(['download', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassListBulkDownload' })">
                            <img src="../../../../assets/img/download.svg">
                        </div>
                        <div class="oval"
                             v-if="hasAccess(['remove', 'class.classes'])"
                             @click="openConfirmDelete">
                            <img src="../../../../assets/img/delete.svg">
                        </div>
                        <div class="oval"
                             v-if="hasAccess(['edit', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassBulkUpdateModal' })">
                            <img src="../../../../assets/img/edit.svg">
                        </div>
                    </control-panel-mobile>
                </template>
                <template v-else>
                    <template v-if="!readonly && selected && selected.length">
                        <div class="oval"
                             v-if="hasAccess(['email', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassBulkEmailModal', options: { width: '680px' } })">
                            <img src="../../../../assets/img/mail.svg">
                        </div>
                       <template v-if="env === 'development' || env === 'test'">
                           <div class="oval"
                                v-if="hasAccess(['sms', 'class.classes'])"
                                @click="openModal({ modalName: 'ClassBulkSMSModal' })">
                               <img src="../../../../assets/img/sms.svg">
                           </div>
                       </template>
                        <div class="oval"
                             v-if="hasAccess(['download', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassListBulkDownload' })">
                            <img src="../../../../assets/img/download.svg">
                        </div>
                        <div class="oval"
                             v-if="hasAccess(['remove', 'class.classes'])"
                             @click="openConfirmDelete">
                            <img src="../../../../assets/img/delete.svg">
                        </div>
                        <div class="oval"
                             v-if="hasAccess(['edit', 'class.classes'])"
                             @click="openModal({ modalName: 'ClassBulkUpdateModal' })">
                            <img src="../../../../assets/img/edit.svg">
                        </div>
                    </template>
                </template>
                <search-box @open-filter="openFilterModal"></search-box>
            </div>
            <template v-if="classList.length">
                <table class="table-page">
                    <thead v-if="!showMobileVersion">
                    <tr>
                        <th>
                            <div v-if="!readonly && accessibleItems.length"
                                 class="checkbox-wrapper">
                                <checkbox :transparent="true"
                                          v-select="classesIds"
                                          :checked="checkedAll">
                                </checkbox>
                            </div>
                        </th>
                        <th v-sort-by>NAME</th>
                        <th>
                            <span v-if="schoolCountry === 'England'">AGE/YEAR</span>
                            <span v-else>AGE/GRADES</span>
                        </th>
                        <th>DAYS</th>
                        <th v-sort-by>LOCATION</th>
                        <th v-sort-by>PRICE</th>
                        <th v-sort-by>ENROLLED</th>
                        <th>DATES</th>
                        <th>STATUS</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <class-row v-for="schoolClass of classList"
                               :key="schoolClass.id"
                               :readonly="readonly"
                               :class-id="schoolClass.id"
                               :class-name="schoolClass.className"
                               :location-name="schoolClass.locationName"
                               :price="schoolClass.price"
                               :max-enroll="schoolClass.maxEnroll"
                               :grade-from="schoolClass.gradeFrom"
                               :grade-to="schoolClass.gradeTo"
                               :age-from="schoolClass.ageFrom"
                               :age-to="schoolClass.ageTo"
                               :status="schoolClass.status"
                               :days-list="daysList(schoolClass)"
                               :children-enrolled="schoolClass.childrenEnrolled"
                               :start-date="schoolClass.classStartDate"
                               :end-date="schoolClass.classEndDate"
                    >
                    </class-row>
                    </tbody>
                </table>
                <pagination :page-sizes-array="PAGE_SIZES"></pagination>
            </template>
            <template v-else-if="!showProgress">
                <empty-page v-if="!searchQuery && !filters"
                            label="No classes added yet"
                            :logo-url="require('../../../../assets/img/class_background.png')">
                </empty-page>
                <div class="not-found" v-else>
                    Oh no! No classes found, please try again.
                    <img src="../../../../assets/img/ilustra-empty-search.png">
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import SelectDirective from '@/directives/SelectDirective';
    import SortByDirective from '@/directives/SortByDirective';
    import { Confirmation } from '@/store/modules/confirm/Confirmation';
    import * as Roles from '@/constants/user-types';
    import { PermissionMixin } from '@/mixins/PermissionMixin';
    import { CLASSES_OPTIONS } from './classes.options.config';
    import { ResizeMobileMixin } from '@/mixins/ResizeMobileMixin';
    import { PAGE_SIZES } from '@/constants/pagination-page-size-increase';

    export default {
        name: 'ScheduleTab',
        components: {
            ClassRow: () => import(/* webpackChunkName: "classes" */ './ClassRow'),
            Pagination: () => import('../../../controls/Pagination'),
            SearchBox: () => import('../../../controls/SearchBox'),
            DayPicker: () => import('../../../controls/DayPicker'),
            Checkbox: () => import('../../../controls/Checkbox'),
            EmptyPage: () => import('../../../controls/EmptyPage'),
            ControlPanelMobile: () => import('../../../controls/ControlPanelMobile')
        },
        directives: { SelectDirective, SortByDirective },
        props: {
            schoolId: {
                type: [String, Number],
                default: this.schoolId
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                Roles,
                PAGE_SIZES,
                env: process.env.NODE_ENV
            }
        },
        computed: {
            ...mapGetters([
                'isMobile',
                'selected',
                'classList',
                'sortBy',
                'searchQuery',
                'checkedAll',
                'filters',
                'showProgress',
                'userRole',
                'scheduleFilter',
                'schoolCountry',
            ]),
            classesIds() {
                return this.classList && this.classList.map(schoolClass => schoolClass.id);
            }
        },
        mixins: [PermissionMixin(CLASSES_OPTIONS), ResizeMobileMixin(992)],
        methods: {
            ...mapActions([
                'getClassList',
                'deleteMultipleClasses',
                'openConfirmDialog',
                'openModal',
                'createSuccessNotification',
                'filterElements'
            ]),
            openFilterModal() {
                this.openModal({
                    modalName: 'FilterModal',
                    options: {
                        filter: 'FilterActivitiesModal'
                    }
                })
            },
            daysList(schoolClass) {
                return [schoolClass.sunday, schoolClass.monday, schoolClass.tuesday, schoolClass.wednesday,
                    schoolClass.thursday, schoolClass.friday, schoolClass.saturday];
            },
            openConfirmDelete() {
                this.selected.length
                    ? this.openConfirmDialog(new Confirmation(this.delete))
                    : this.createWarningNotification('Please select at least one class');
            },
            async delete() {
                const deletedMoreThanOne = this.selected.length > 1;

                await this.deleteMultipleClasses();
                this.createSuccessNotification(`You’ve successfully deleted ${deletedMoreThanOne ? 'classes' : 'a class'}`);
            }
        },
        created() {
            (this.scheduleFilter && !_.isEmpty(this.scheduleFilter) &&
                (!_.isEmpty(this.scheduleFilter.ages) || !_.isEmpty(this.scheduleFilter.grades) || !_.isEmpty(this.scheduleFilter.statuses) ||
                    this.scheduleFilter.dateFrom != null || this.scheduleFilter.dateTo != null ||
                    this.scheduleFilter.sunday || this.scheduleFilter.monday || this.scheduleFilter.tuesday ||
                    this.scheduleFilter.wednesday || this.scheduleFilter.thursday || this.scheduleFilter.friday ||
                    this.scheduleFilter.saturday)) && this.filterElements(this.scheduleFilter);
            const { schoolId } = this;
            this.getClassList({ schoolId });
        },
        beforeRouteLeave(to, from, next) {
            this.removeStats();
            next();
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../assets/colors";
    @import "../../../../assets/mixins";

    table {
        /deep/ td, /deep/ th {
            &:first-child {
                width: 5%;
            }

            &:nth-child(2) {
                width: 19%;
                min-width: 160px;
            }

            &:nth-child(3) {
                width: 7%;
                white-space: pre-line;
                min-width: 140px;
            }

            &:nth-child(4) {
                width: 18%;
                min-width: 220px;
            }

            &:nth-child(5) {
                width: 13%;
                @media screen and (max-width: 1200px) {
                    width: 11%;
                }
            }

            &:nth-child(6) {
                width: 7%;
                line-height: 1.71;
            }

            &:nth-child(7) {
                padding-left: 3%;
                width: 10%;
            }

            &:nth-child(8) {
                width: 8%;
            }

            &:nth-child(9) {
                width: 9%;
                @media screen and (max-width: 1200px) {
                    width: 11%;
                }
            }

            &:last-child {
                width: 5%;
                padding-right: 30px;
            }
        }

        /deep/ td {
            &:nth-child(2) {
                cursor: pointer;
                color: $SoftBlue;
                font-size: 16px;
                font-weight: 600;
                line-height: 1.5;
            }

            &:nth-child(3) {
                font-size: 14px;
                line-height: 1.71;
                color: $Dark;
            }

            &:nth-child(6) {
                font-weight: bold;
                color: $Dark;
            }
        }

        @include table-body-mobile($minMax: 320px);
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/classes/ClassList.vue